import { useState, type ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons/faExclamationCircle';
import { targetBrandConfig } from 'AppBranding';
import { Button } from 'Button';
import { Text } from 'Text';
import { useErrorLogService } from 'ErrorLogging';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import { navigationHelper } from 'NavigationHelpers';
import { subscriptionIncomplete, subscriptionPastDue } from 'Subscription';
import { paymentPerInterval, tuitionContractComplete, tuitionContractLikelyHasNoInvoices } from 'TuitionContract';
import { usesRecurringPayments } from 'TuitionPlan';
import { formatCurrencyForDisplay } from 'Currency';
import { MailtoLink } from 'MailtoLink';
import { getCompletedStripeCheckoutSession, getTuitionContract } from 'Users';
import { stripeBillingPortalApi } from '../StripeBillingPortalApi';
import { LOCALE_PREFIX } from '../../tuition_and_registration/constants';

type BillingView = {
    // Drive the card tint and the button colour.
    isIncomplete?: boolean;
    isPastDue?: boolean;
    newBrowserKey?: string;
    warningMessageKey?: string;
    warningDescriptionKey?: string;
    // Names the plan and its per-interval amount. Past due only, keyed by the plan frequency.
    billedAtKey?: string;
    billingMessageKey?: string;
    processingPaymentMessageKey?: string;
    buttonKey?: string;
};

export function RegisteredManageBilling(): ReactElement | null {
    const currentUser = useRequiredCurrentUser();
    const ErrorLogService = useErrorLogService();
    const { loadUrl } = navigationHelper();
    const [createBillingPortalSession] = stripeBillingPortalApi.useCreateBillingPortalSessionMutation();
    const [stripeProcessing, setStripeProcessing] = useState(false);

    const needsToOpenInBrowser = !!window.CORDOVA;
    const tuitionContract = getTuitionContract(currentUser);
    const subscription = tuitionContract?.subscription;

    const paymentAmountPerInterval = formatCurrencyForDisplay(paymentPerInterval(tuitionContract));
    const view = getBillingView({
        needsToOpenInBrowser,
        subscriptionIsIncomplete: subscriptionIncomplete(subscription),
        subscriptionIsPastDue: subscriptionPastDue(subscription),
        hasSubscription: !!subscription,
        userPaidOutsideOfStripe: currentUser.paidOutsideOfStripe,
        contractComplete: tuitionContractComplete(tuitionContract),
        contractLikelyHasNoInvoices: tuitionContractLikelyHasNoInvoices(tuitionContract),
        completedCheckoutSession: getCompletedStripeCheckoutSession(currentUser),
        planFrequency: tuitionContract ? tuitionContract.tuitionPlan.frequency : null,
        usesRecurring: !!tuitionContract && usesRecurringPayments(tuitionContract.tuitionPlan),
    });

    const billingEmail = targetBrandConfig(currentUser).emailAddressForUsername('billing');
    const nextPaymentDate = subscription?.currentPeriodEnd
        ? new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
          })
        : null;

    const messageValues = {
        paymentPerInterval: paymentAmountPerInterval,
        nextPaymentDate: nextPaymentDate ?? '',
    };

    const openStripeBillingPortal = async () => {
        setStripeProcessing(true);
        try {
            const response = await createBillingPortalSession().unwrap();
            const url = response?.session?.url;
            if (!url) {
                setStripeProcessing(false);
                return;
            }

            // On Cordova the portal opens in a new window; otherwise it replaces this tab.
            if (needsToOpenInBrowser) {
                loadUrl(url, '_blank');
                setStripeProcessing(false);
            } else {
                window.location.href = url;
            }
        } catch (error) {
            setStripeProcessing(false);
            ErrorLogService.notify('Failed to create a Stripe billing portal session', error);
        }
    };

    return (
        <BillingCard
            view={view}
            messageValues={messageValues}
            billingEmail={billingEmail}
            stripeProcessing={stripeProcessing}
            onManageBilling={openStripeBillingPortal}
        />
    );
}

type BillingCardProps = {
    view: BillingView;
    messageValues: Record<string, string>;
    billingEmail: string;
    stripeProcessing: boolean;
    onManageBilling: () => void;
};

function BillingCard({
    view,
    messageValues,
    billingEmail,
    stripeProcessing,
    onManageBilling,
}: BillingCardProps): ReactElement {
    const { t } = useTranslation('back_royal');

    /*
        `registered_contact_support` carries a <mail/> slot. The rest of these keys are plain text
        with <strong>, which <Trans> keeps by default, so one render covers all of them.
    */
    const message = (i18nKey: string) => (
        <Trans
            t={t}
            i18nKey={i18nKey}
            values={messageValues}
            components={{ mail: <MailtoLink className="text-sm" email={billingEmail} /> }}
        />
    );

    const tintClasses = view.isPastDue
        ? 'border-coral/50 bg-coral/10'
        : view.isIncomplete
          ? 'border-yellow/50 bg-yellow/10'
          : 'border-slate-grey-200 bg-slate-grey-50';

    const accentTextClass = view.isPastDue ? 'text-coral' : 'text-yellow';

    // Both headings are written out in full, because Tailwind only compiles a class it can read here.
    const warningHeadingClasses = view.isPastDue
        ? 'mb-0 mt-2 text-center text-md font-semibold text-coral'
        : 'mb-0 mt-2 text-center text-md font-semibold text-yellow';
    const messageKey = view.billingMessageKey ?? view.processingPaymentMessageKey;
    const hasSideBySideRow = !!messageKey && !!view.buttonKey;

    return (
        <div className={`rounded-5 border p-5 ${tintClasses}`}>
            {view.newBrowserKey ? (
                <Text as="p" className="mb-4 text-center text-sm">
                    {t(view.newBrowserKey)}
                </Text>
            ) : null}

            {view.warningMessageKey ? (
                <div className="mb-4 text-center">
                    {/* Matches the warning icon in useProjectStatusConfig. */}
                    <FontAwesomeIcon icon={faExclamationCircle} className={`text-xl ${accentTextClass}`} />
                    <Text as="h2" className={warningHeadingClasses}>
                        {t(view.warningMessageKey)}
                    </Text>
                    {view.warningDescriptionKey ? (
                        <Text as="p" className="mb-0 mt-1 text-sm">
                            {message(view.warningDescriptionKey)}
                        </Text>
                    ) : null}

                    {view.billedAtKey ? (
                        <Text as="p" className="mb-0 mt-3 text-sm">
                            {message(view.billedAtKey)}
                        </Text>
                    ) : null}
                </div>
            ) : null}

            {/*
                The message sits beside the button on desktop and stacks above it on mobile. When
                only one of the two is present it is centred instead.
            */}
            <div
                className={`flex flex-col gap-4 sm:flex-row sm:items-center ${
                    hasSideBySideRow ? 'sm:justify-between' : 'sm:justify-center'
                }`}
            >
                {messageKey ? (
                    <Text
                        as="p"
                        // mb-0: a <p> in this page picks up a 15px bottom margin from the global
                        // stylesheet, which would stop `items-center` from centring it on the button.
                        className={`mb-0 text-sm ${hasSideBySideRow ? 'sm:flex-1 sm:text-start' : 'text-center'}`}
                    >
                        {message(messageKey)}
                    </Text>
                ) : null}

                {view.buttonKey ? (
                    <Button
                        type="button"
                        color={view.isPastDue ? 'red' : 'slate-grey'}
                        height={9}
                        // The library's solid slate-grey is a pale fill with dark text. The billing
                        // action reads as the primary control here, so use the darker slate.
                        className={twMerge('w-full sm:w-56', !view.isPastDue && 'bg-slate-grey text-white')}
                        isDisabled={stripeProcessing}
                        onPress={onManageBilling}
                    >
                        {t(stripeProcessing ? `${LOCALE_PREFIX}.processing` : view.buttonKey)}
                    </Button>
                ) : null}
            </div>
        </div>
    );
}

type BillingViewInputs = {
    needsToOpenInBrowser: boolean;
    subscriptionIsIncomplete: boolean;
    subscriptionIsPastDue: boolean;
    hasSubscription: boolean;
    userPaidOutsideOfStripe: boolean;
    contractComplete: boolean;
    contractLikelyHasNoInvoices: boolean;
    completedCheckoutSession: boolean;
    planFrequency: string | null;
    usesRecurring: boolean;
};

/*
    The branch order matters, because each one is an `else if`. Two of them are not obvious: any
    Subscription is checked before paid-outside-Stripe, since some users have paid both ways; and a
    complete contract gets no payment-history button, because Stripe raises no invoice for a one-time
    purchase.
*/
function getBillingView(inputs: BillingViewInputs): BillingView {
    if (inputs.needsToOpenInBrowser) {
        return {
            newBrowserKey: `${LOCALE_PREFIX}.new_browser_payment_details`,
            buttonKey: `${LOCALE_PREFIX}.launch_browser`,
        };
    }

    if (inputs.subscriptionIsIncomplete) {
        return {
            isIncomplete: true,
            warningMessageKey: `${LOCALE_PREFIX}.action_required`,
            warningDescriptionKey: `${LOCALE_PREFIX}.confirm_payment`,
            billingMessageKey: `${LOCALE_PREFIX}.additional_steps_required`,
        };
    }

    if (inputs.subscriptionIsPastDue) {
        return {
            isPastDue: true,
            buttonKey: `${LOCALE_PREFIX}.registered_manage_billing_payment_trouble`,
            warningMessageKey: `${LOCALE_PREFIX}.trouble_processing_payment`,
            warningDescriptionKey: `${LOCALE_PREFIX}.please_re_enter_payment_details`,
            // No `once` key: a one-time plan cannot be past due.
            billedAtKey: inputs.planFrequency
                ? `${LOCALE_PREFIX}.currently_enrolled_in_x_billed_at_x_${inputs.planFrequency}_during`
                : undefined,
        };
    }

    if (inputs.hasSubscription) {
        // All recurring payment plans share one locale key.
        const descriptor = inputs.usesRecurring ? 'recurring' : inputs.planFrequency;
        return {
            billingMessageKey: `${LOCALE_PREFIX}.registered_payment_smallprint_${descriptor}`,
            buttonKey: `${LOCALE_PREFIX}.registered_manage_billing`,
        };
    }

    if (inputs.userPaidOutsideOfStripe) {
        return { billingMessageKey: `${LOCALE_PREFIX}.registered_contact_support` };
    }

    if (inputs.contractComplete) {
        return {
            billingMessageKey: `${LOCALE_PREFIX}.narrative_all_paid`,
            buttonKey: inputs.contractLikelyHasNoInvoices
                ? undefined
                : `${LOCALE_PREFIX}.registered_manage_billing_view_payment_history`,
        };
    }

    if (inputs.completedCheckoutSession) {
        return { processingPaymentMessageKey: `${LOCALE_PREFIX}.registered_processing_payment` };
    }

    return {};
}
